<template>
  <div class="app-wrapper">
    <div v-if="showSessionExpiredModal" class="session-expired-overlay">
      <div class="session-expired-modal">
        <base-custom-alert cardClassColor="bg-warning-light" bgClassColor="bg-warning">
          <template #customAlertIcon>
            <i class="bi bi-exclamation-triangle-fill h4"></i>
          </template>
          <template #customAlertText>
            <div class="session-expired-content">
              <button
                type="button"
                class="btn-close session-expired-close"
                aria-label="Close"
                @click="closeSessionExpiredModal"
              ></button>
              <h6 class="text-center">Novo Início de Sessão Detetado!</h6>
              <p class="text-center">Necessário Efetuar Novo Login!</p>
            </div>
          </template>
        </base-custom-alert>
      </div>
    </div>
    <the-header v-if="isAuthorized" />
    <main ref="appMain" v-if="!showMaintenanceModal" class="theme-light-radial-gradient main-content">
      <div v-if="isAuthorized && !isOnLine">
        <p class="rounded-0 shadow-danger bg-danger text-center mb-3">
          Sem acesso ao servidor
        </p>
      </div>
      <NotificationModal />
      <router-view />
    </main>
    <the-footer v-if="isAuthorized" />
    <MaintenanceModal v-if="showMaintenanceModal" @close="closeModal" />
    <DeviceIncompatibilityModal :isVisible="showDeviceIncompatibilityModal" :appType="deviceIncompatibilityAppType || 2"
      @close="closeDeviceModal" />
  </div>
</template>

<script>
import { nextTick } from "vue";
import TheHeader from "@/components/layout/TheHeader.vue";
import TheFooter from "@/components/layout/TheFooter.vue";
import NotificationModal from "@/pages/Notification/NotificationsModal.vue";
import BaseCustomAlert from "@/ui/BaseCustomAlert.vue";
import { mapState, mapMutations } from "vuex";
import MaintenanceModal from "@/components/MaintenanceModal.vue";
import DeviceIncompatibilityModal from "@/components/DeviceIncompatibilityModal.vue";

export default {
  data() {
    return {
      sessionExpiredModalTimerId: null,
    };
  },
  components: {
    BaseCustomAlert,
    TheHeader,
    TheFooter,
    NotificationModal,
    MaintenanceModal,
    DeviceIncompatibilityModal,
  },
  created() {
    this.$store.dispatch("tryLogin");
    this.syncSSEConnection();
    document.title = "GEO OPS";
  },
  unmounted() {
    this.$store.dispatch("closeSSE");
  },
  mounted() {
    this.scheduleLayoutSync();
    window.addEventListener("resize", this.syncLayoutSpacing);
    this.handleWindowLoad = () => {
      this.scheduleLayoutSync();
    };
    window.addEventListener("load", this.handleWindowLoad);
    this.layoutSyncTimeoutId = window.setTimeout(() => {
      this.scheduleLayoutSync();
    }, 120);
  },
  beforeUnmount() {
    window.removeEventListener("resize", this.syncLayoutSpacing);
    if (this.handleWindowLoad) {
      window.removeEventListener("load", this.handleWindowLoad);
    }
    if (this.layoutSyncTimeoutId) {
      window.clearTimeout(this.layoutSyncTimeoutId);
    }
    if (this.sessionExpiredModalTimerId) {
      window.clearTimeout(this.sessionExpiredModalTimerId);
      this.sessionExpiredModalTimerId = null;
    }
  },

  computed: {
    ...mapState([
      "showMaintenanceModal",
      "showDeviceIncompatibilityModal",
      "deviceIncompatibilityAppType",
    ]),
    didAutoLogout() {
      return this.$store.getters.didAutoLogout;
    },
    isAuthorized() {
      return this.$store.getters.isAuthorized;
    },
    currentUsername() {
      return this.$store.getters.getUserDetailList?.usr_username || null;
    },
    currentUserToken() {
      return this.$store.getters.getUserSession?.app_token || null;
    },
    isOnLine() {
      return this.$store.getters.getOnline;
    },
    showSessionExpiredModal() {
      return this.$store.getters.showSessionExpiredModal;
    },
  },
  methods: {
    ...mapMutations([
      "SHOW_MAINTENANCE_MODAL",
      "SHOW_DEVICE_INCOMPATIBILITY_MODAL",
      "SET_DEVICE_INCOMPATIBILITY_APP_TYPE",
    ]),
    closeModal() {
      this.SHOW_MAINTENANCE_MODAL(false);
    },
    closeDeviceModal() {
      this.SHOW_DEVICE_INCOMPATIBILITY_MODAL(false);
      this.SET_DEVICE_INCOMPATIBILITY_APP_TYPE(null);
    },
    async closeSessionExpiredModal() {
      if (this.sessionExpiredModalTimerId) {
        window.clearTimeout(this.sessionExpiredModalTimerId);
        this.sessionExpiredModalTimerId = null;
      }
      this.$store.commit("setShowSessionExpiredModal", false);
    },
    async handleSessionExpiredFlow() {
      await this.$router.replace("/loginOPS");

      if (this.sessionExpiredModalTimerId) {
        window.clearTimeout(this.sessionExpiredModalTimerId);
      }

      this.sessionExpiredModalTimerId = window.setTimeout(() => {
        this.closeSessionExpiredModal();
      }, 5000);
    },
    syncSSEConnection() {
      if (this.isAuthorized && this.currentUsername && this.currentUserToken) {
        this.$store.dispatch("initSSE");
        return;
      }

      this.$store.dispatch("closeSSE");
    },
    scheduleLayoutSync() {
      nextTick(() => {
        this.syncLayoutSpacing();
      });
    },
    syncLayoutSpacing() {
      const mainElement = this.$refs.appMain;

      if (!mainElement) {
        return;
      }

      const viewportHeight = window.innerHeight || document.documentElement.clientHeight || 0;
      const headerElement = document.querySelector("header.header.position-fixed");
      const footerElement = document.querySelector("footer.footer");
      const footerInfoElement = document.querySelector(".footer-info");
      const mainContainers = document.querySelectorAll(".main-container");
      const headerHeight = headerElement?.offsetHeight || 0;
      const footerHeight = footerElement?.offsetHeight || 0;
      const footerInfoHeight = footerInfoElement?.offsetHeight || 0;

      if (!this.isAuthorized) {
        mainElement.style.minHeight = `${viewportHeight}px`;
        mainElement.style.paddingTop = "0px";
        mainElement.style.paddingBottom = "0px";
        mainContainers.forEach((container) => {
          container.style.minHeight = "";
        });
        return;
      }

      mainElement.style.paddingTop = headerHeight > 0 ? `${headerHeight + 15}px` : "0px";
      mainElement.style.paddingBottom = footerHeight > 0 ? `${footerHeight + 0}px` : "0px";

      mainElement.style.minHeight = `${viewportHeight}px`;
      if (footerInfoHeight > 0 && footerHeight === 0) {
        mainElement.style.minHeight = `${Math.max(viewportHeight - footerInfoHeight, 0)}px`;
      }

      mainContainers.forEach((container) => {
        container.style.minHeight = "";
      });
    },
  },
  watch: {
    didAutoLogout(curValue, oldValue) {
      if (curValue && curValue !== oldValue && !this.showSessionExpiredModal) {
        this.$router.replace("/loginOPS");
      }
      this.scheduleLayoutSync();
    },
    isAuthorized(curValue, oldValue) {
      if (!curValue && oldValue && !this.showSessionExpiredModal) {
        this.$router.replace("/loginOPS");
      }
      this.syncSSEConnection();
      this.scheduleLayoutSync();
    },
    currentUsername() {
      this.syncSSEConnection();
    },
    currentUserToken() {
      this.syncSSEConnection();
    },
    "$route.fullPath"() {
      this.scheduleLayoutSync();
    },
    showMaintenanceModal() {
      this.scheduleLayoutSync();
    },
    showSessionExpiredModal(curValue, oldValue) {
      if (curValue && curValue !== oldValue) {
        this.handleSessionExpiredFlow();
      }
    },
  },
};
</script>

<style>
.app-wrapper {
  min-height: 100vh;
  display: flex;
  flex-direction: column;
}

.main-content {
  flex: 1;
  display: flex;
  flex-direction: column;
  min-height: 0;
  box-sizing: border-box;
}

header.header {
  z-index: 1000;
  width: 100%;
  top: 0;
  left: 0;
}

footer {
  width: 100%;
}

.session-expired-overlay {
  position: fixed;
  inset: 0;
  z-index: 5000;
  display: flex;
  align-items: flex-start;
  justify-content: center;
  padding: 16px;
  padding-top: 20px;
  background: rgba(0, 0, 0, 0.35);
  backdrop-filter: blur(2px);
}

.session-expired-modal {
  width: min(430px, 100%);
}

.session-expired-content {
  position: relative;
  padding-top: 8px;
  padding-right: 12px;
}

.session-expired-close {
  position: absolute;
  top: 0;
  right: 0;
}
</style>
